import React, { type ReactNode, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useNavigate } from '@tanstack/react-router'
import { useLocalStorage, useSessionStorage } from 'usehooks-ts'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser.ts'
import { type NavigationLink } from '@/components/organisms/Navigation'
import { type AppViewProps, type AppViewState } from '@/components/templates/AppView'
import { useRegisterSupportPage } from '@/hooks/useSupportContext'
import { type TransportRequest } from '@/model/contexts/TransportRequestsProvider.tsx'
import { AppPageMapContext } from '@/pages/dashboard/AppPageMapProvider.tsx'

import { transportRequestType } from './data/transportRequestType.ts'
import { useSelectedTeam } from './state/useSelectedTeam.ts'

export type AppPageRequestStatus = NavigationLink['value']
export type AppPageStateActions =
  | 'onNavigationChanged'
  | 'onViewChanged'
  | 'onTelevisionToggled'
  | 'onActivate'
  | 'onAssignManually'
  | 'onClose'
  | 'onTeamSelected'
  | 'onSearchChanged'
  | 'onNavigationSortChanged'
  | 'onTeamSortByChanged'
  | 'onStartingLocationChanged'
  | 'onBaseChanged'
  | 'onMenuFilterChanged'
  | 'onSuperPower'

type AppPageState = AppViewState & {
  searchQuery?: string
}

export type AppPageStateValue = Pick<AppViewProps, AppPageStateActions> & {
  state: AppPageState
  onHighlightInternal: (transportRequest: TransportRequest) => void
}

const DEFAULT_APP_STATE: AppPageState = {
  television: false,
  view: 'list',
  activeStatus: 'pending',
  teamSortBy: 'asc',
  requestSortBy: 'asc',
  completedRequestSortBy: 'desc',
}

export const AppPageStateContext = React.createContext<AppPageStateValue>({
  state: DEFAULT_APP_STATE,
  onViewChanged() {},
  onNavigationChanged() {},
  onTelevisionToggled() {},
  onActivate() {},
  onAssignManually() {},
  onClose() {},
  onTeamSelected() {},
  onNavigationSortChanged() {},
  onTeamSortByChanged() {},
  onSearchChanged() {},
  onStartingLocationChanged() {},
  onBaseChanged() {},
  onMenuFilterChanged() {},
  onHighlightInternal() {},
})

export const AppPageStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { focusTransportRequest, focusRegion, refresh } = useContext(AppPageMapContext)

  const [television, setTelevision] = useSessionStorage<AppPageState['television']>(
    'television',
    DEFAULT_APP_STATE.television
  )
  const [teamSortBy, setTeamSortBy] = useLocalStorage<AppPageState['teamSortBy']>(
    'teamSortBy',
    DEFAULT_APP_STATE.teamSortBy
  )
  const [requestSortBy, setRequestSortBy] = useLocalStorage<AppPageState['requestSortBy']>(
    'requestSortBy',
    DEFAULT_APP_STATE.requestSortBy
  )
  const [completedRequestSortBy, setCompletedRequestSortBy] = useLocalStorage<AppPageState['completedRequestSortBy']>(
    'completedRequestSortBy',
    DEFAULT_APP_STATE.completedRequestSortBy
  )
  const [searchQuery, setSearchQuery] = useState<AppPageState['searchQuery']>()
  const [selectedStartingLocation, setSelectedStartingLocation] = useLocalStorage<
    AppPageState['selectedStartingLocation']
  >('selectedStartingLocation', 'all')
  const [selectedBase, setSelectedBase] = useSessionStorage<AppPageState['selectedBase']>('selectedBase', 'all')
  const [mapPendingTeamStatusActiveFilters, setMapPendingTeamStatusActiveFilters] = useSessionStorage<
    AppPageState['mapPendingTeamStatusActiveFilters']
  >('mapPendingTeamStatusActiveFilters', {})

  const [navigationSelection, setNavigationSelection] = useSessionStorage<
    Pick<AppPageStateValue['state'], 'activeStatus' | 'view' | AppPageRequestStatus> & { selectedTeamId?: string }
  >('navigationSelection', { activeStatus: DEFAULT_APP_STATE.activeStatus, view: DEFAULT_APP_STATE.view })

  const selectedTeam = useSelectedTeam(navigationSelection)

  const intl = useIntl()
  useRegisterSupportPage({
    detail: intl.formatMessage({ id: `soutien.page.operations.${navigationSelection.activeStatus}` }),
    object: null,
  })

  const { sys_admin: isSysAdmin } = useAuthenticatedFirebaseUser()
  const navigate = useNavigate()

  const actions = useMemo<Pick<AppViewProps, AppPageStateActions> & Pick<AppPageStateValue, 'onHighlightInternal'>>(
    () => ({
      onTelevisionToggled: () => {
        setTelevision((prev) => {
          if (!prev) {
            setTimeout(focusRegion, 200)
          }
          return !prev
        })
      },
      onViewChanged: (view) => {
        setNavigationSelection((state) => ({ ...state, view }))
        if (view === 'map') {
          setTimeout(refresh, 200)
        }
      },
      onNavigationSortChanged: (status, value) => {
        if (status === 'completed') {
          setCompletedRequestSortBy(value)
        } else {
          setRequestSortBy(value)
        }
      },
      onTeamSortByChanged: setTeamSortBy,
      onSearchChanged: setSearchQuery,
      onStartingLocationChanged: setSelectedStartingLocation,
      onBaseChanged: setSelectedBase,
      onHighlightInternal: (transportRequest) => {
        const status = transportRequestType(transportRequest)
        if (status === 'invalid_type') {
          return
        }
        setNavigationSelection((state) => ({
          ...state,
          view: 'list',
          activeStatus: status,
          [status]: { highlighted: transportRequest._id },
          selectedTeamId: undefined,
        }))
        setTimeout(() => {
          setNavigationSelection((state) => ({ ...state, [status]: undefined }))
        }, 3000)
      },
      onNavigationChanged: (status) => {
        setNavigationSelection((state) => ({ activeStatus: status, view: state.view }))
      },
      onActivate: (id) => {
        setNavigationSelection((state) => {
          const toggleOffActive = state[state.activeStatus]?.active === id
          if (state.view === 'map') {
            focusTransportRequest(toggleOffActive ? undefined : id)
          }
          return {
            ...state,
            [state.activeStatus]: toggleOffActive ? undefined : { active: id },
            selectedTeamId: undefined,
          }
        })
      },
      onAssignManually: (id) => {
        setNavigationSelection((state) => ({ ...state, [state.activeStatus]: { selected: id } }))
      },
      onClose: () => {
        setNavigationSelection((state) => ({ activeStatus: state.activeStatus, view: state.view }))
      },
      onTeamSelected: (teamId) => {
        setNavigationSelection((state) => {
          const selection = state[state.activeStatus]
          return selection && 'selected' in selection ? { ...state, selectedTeamId: teamId } : state
        })
      },
      onMenuFilterChanged: (id, checked) => {
        setMapPendingTeamStatusActiveFilters((state) => ({ ...state, [id]: checked }))
      },
      onSuperPower: !isSysAdmin ? undefined : () => navigate({ to: '/regions' }),
    }),
    [
      setTeamSortBy,
      setSelectedStartingLocation,
      setSelectedBase,
      isSysAdmin,
      setTelevision,
      focusRegion,
      setNavigationSelection,
      refresh,
      setCompletedRequestSortBy,
      setRequestSortBy,
      focusTransportRequest,
      setMapPendingTeamStatusActiveFilters,
      navigate,
    ]
  )

  const value = useMemo(
    () => ({
      ...actions,
      state: {
        television,
        teamSortBy,
        requestSortBy,
        completedRequestSortBy,
        searchQuery,
        selectedStartingLocation,
        selectedBase,
        mapPendingTeamStatusActiveFilters,
        ...navigationSelection,
        ...selectedTeam,
      },
    }),
    [
      actions,
      completedRequestSortBy,
      mapPendingTeamStatusActiveFilters,
      navigationSelection,
      requestSortBy,
      searchQuery,
      selectedBase,
      selectedStartingLocation,
      selectedTeam,
      teamSortBy,
      television,
    ]
  )

  return <AppPageStateContext.Provider value={value}>{children}</AppPageStateContext.Provider>
}
